import {
  ChainId,
  Currency,
  CurrencyAmount,
  Price,
  Token,
  TradeType,
} from "@uniswap/sdk-core";
import { useWeb3React } from "@web3-react/core";
import tryParseCurrencyAmount from "lib/utils/tryParseCurrencyAmount";
import { useMemo, useRef } from "react";
import {
  ClassicTrade,
  INTERNAL_ROUTER_PREFERENCE_PRICE,
} from "state/routing/types";
import { useRoutingAPITrade } from "state/routing/useRoutingAPITrade";

import { SupportedInterfaceChain, asSupportedChain } from "constants/chains";
import {
  CUSD_CELO,
  CUSD_CELO_ALFAJORES,
  DAI_OPTIMISM,
  USDC_ARBITRUM,
  USDC_ARBITRUM_GOERLI,
  USDC_AVALANCHE,
  USDC_BASE,
  USDC_GOERLI,
  USDC_MAINNET,
  USDC_OPTIMISM_GOERLI,
  USDC_POLYGON,
  USDC_POLYGON_MUMBAI,
  USDC_SEPOLIA,
  USDT_BSC,
  GUSDC_GLUE_DEVNET,
  GLUE_USD_GLUE_MAINNET,
} from "../constants/tokens";

// Stablecoin amounts used when calculating spot price for a given currency.
// The amount is large enough to filter low liquidity pairs.
// @ts-ignore
export const STABLECOIN_AMOUNT_OUT: {
  [key in SupportedInterfaceChain]: CurrencyAmount<Token>;
} = {
  [ChainId.MAINNET]: CurrencyAmount.fromRawAmount(USDC_MAINNET, 100000000000),
  [ChainId.ARBITRUM_ONE]: CurrencyAmount.fromRawAmount(
    USDC_ARBITRUM,
    10000000000
  ),
  [ChainId.OPTIMISM]: CurrencyAmount.fromRawAmount(DAI_OPTIMISM, 1e22),
  [ChainId.POLYGON]: CurrencyAmount.fromRawAmount(USDC_POLYGON, 10000000000),
  [ChainId.CELO]: CurrencyAmount.fromRawAmount(CUSD_CELO, 1e22),
  [ChainId.BNB]: CurrencyAmount.fromRawAmount(USDT_BSC, 100e18),
  [ChainId.AVALANCHE]: CurrencyAmount.fromRawAmount(
    USDC_AVALANCHE,
    10000000000
  ),
  [ChainId.BASE]: CurrencyAmount.fromRawAmount(USDC_BASE, 10000000000),
  [ChainId.GOERLI]: CurrencyAmount.fromRawAmount(USDC_GOERLI, 10000000000),
  [ChainId.SEPOLIA]: CurrencyAmount.fromRawAmount(USDC_SEPOLIA, 10000000000),
  [ChainId.OPTIMISM_GOERLI]: CurrencyAmount.fromRawAmount(
    USDC_OPTIMISM_GOERLI,
    10000000000
  ),
  [ChainId.ARBITRUM_GOERLI]: CurrencyAmount.fromRawAmount(
    USDC_ARBITRUM_GOERLI,
    10000000000
  ),
  [ChainId.POLYGON_MUMBAI]: CurrencyAmount.fromRawAmount(
    USDC_POLYGON_MUMBAI,
    10000000000
  ),
  [ChainId.CELO_ALFAJORES]: CurrencyAmount.fromRawAmount(
    CUSD_CELO_ALFAJORES,
    10000000000
  ),
  // TODO: add glue mainnet
  [ChainId.GLUE_DEVNET]: CurrencyAmount.fromRawAmount(GUSDC_GLUE_DEVNET, 1e22),
  // [ChainId.GLUE_TESTNET]: CurrencyAmount.fromRawAmount(
  //   GL_USD_GLUE_TESTNET,
  //   1e22
  // ),
  [ChainId.GLUE_MAINNET]: CurrencyAmount.fromRawAmount(GLUE_USD_GLUE_MAINNET, 1e22),
};

/**
 * Returns the price in USDC of the input currency
 * @param currency currency to compute the USDC price of
 */
export default function useStablecoinPrice(
  currency?: Currency
): Price<Currency, Token> | undefined {
  const chainId = asSupportedChain(currency?.chainId);
  const amountOut = chainId ? STABLECOIN_AMOUNT_OUT[chainId] : undefined;
  const stablecoin = amountOut?.currency;

  const { trade } = useRoutingAPITrade(
    false /* skip */,
    TradeType.EXACT_OUTPUT,
    amountOut,
    currency,
    INTERNAL_ROUTER_PREFERENCE_PRICE
  );
  const price = useMemo(() => {
    if (!currency || !stablecoin) {
      return undefined;
    }

    // handle usdc
    if (currency?.wrapped.equals(stablecoin)) {
      return new Price(stablecoin, stablecoin, "1", "1");
    }

    // if initial quoting fails, we may end up with a DutchOrderTrade
    if (trade && trade instanceof ClassicTrade) {
      const { numerator, denominator } = trade.routes[0].midPrice;
      return new Price(currency, stablecoin, denominator, numerator);
    }

    return undefined;
  }, [currency, stablecoin, trade]);

  const lastPrice = useRef(price);
  if (
    !price ||
    !lastPrice.current ||
    !price.equalTo(lastPrice.current) ||
    !price.baseCurrency.equals(lastPrice.current.baseCurrency)
  ) {
    lastPrice.current = price;
  }
  return lastPrice.current;
}

export function useStablecoinValue(
  currencyAmount: CurrencyAmount<Currency> | undefined | null
) {
  const price = useStablecoinPrice(currencyAmount?.currency);

  return useMemo(() => {
    if (!price || !currencyAmount) return null;
    try {
      return price.quote(currencyAmount);
    } catch (error) {
      return null;
    }
  }, [currencyAmount, price]);
}

/**
 *
 * @param fiatValue string representation of a USD amount
 * @returns CurrencyAmount where currency is stablecoin on active chain
 */
export function useStablecoinAmountFromFiatValue(
  fiatValue: number | null | undefined
) {
  const { chainId } = useWeb3React();
  const supportedChainId = asSupportedChain(chainId);
  const stablecoin = supportedChainId
    ? STABLECOIN_AMOUNT_OUT[supportedChainId].currency
    : undefined;

  return useMemo(() => {
    if (
      fiatValue === null ||
      fiatValue === undefined ||
      !chainId ||
      !stablecoin
    ) {
      return undefined;
    }

    // trim for decimal precision when parsing
    const parsedForDecimals = fiatValue.toFixed(stablecoin.decimals).toString();
    try {
      // parse USD string into CurrencyAmount based on stablecoin decimals
      return tryParseCurrencyAmount(parsedForDecimals, stablecoin);
    } catch (error) {
      return undefined;
    }
  }, [chainId, fiatValue, stablecoin]);
}
